import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { DuplicateCarnetsBanner } from "../components/carnet/DuplicateCarnetsBanner";
import { CarnetEligibilityReport } from "../components/carnet/CarnetEligibilityReport";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { beltHex as beltHexShared } from "../lib/beltColor";
import { toast } from "sonner";
import {
  BookOpen, Plus, AlertTriangle, CheckCircle2, Trophy,
  Star, Sparkles, Activity, Users, Loader2, ChevronRight, X, TrendingUp, Trash2,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { PageHeader } from "../components/ui/kaizen";

const DISCIPLINES = [
  { value: "muay_thai_kickboxing", label: "Kickboxing / Muay Thaï", dot: "bg-red-500" },
  { value: "bjj", label: "Jiu-Jitsu Brésilien", dot: "bg-blue-500" },
  { value: "jujutsu_japonais", label: "Jiu-jitsu Can Ryu", dot: "bg-emerald-500" },
  { value: "petits_samourais", label: "Petits Samouraïs (3-6 ans)", dot: "bg-amber-400" },
];
const DISC_LABEL = (v) => DISCIPLINES.find((d) => d.value === v)?.label || v;

// 🧪 Pilote PCR (J-11) — groupe pédagogique en LECTURE SEULE pour les carnets sélectionnés (aucune écriture)
function PilotGroupBadge({ profileId, belt }) {
  const [p, setP] = useState(null);
  const [eq, setEq] = useState(false);
  const [uni, setUni] = useState(false);
  const load = () => api.get(`/ops/academy-structure/pilot/profile/${profileId}`).then((r) => setP(r.data)).catch(() => setP(null));
  useEffect(() => { setP(null); setEq(false); setUni(false); if (profileId) load(); }, [profileId, belt]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!p?.in_pilot) return null;
  const validate = async () => {
    const name = window.prompt(`🔒 Validation du Kru — entrée dans « ${p.group.name} »\n\nTape exactement le nom de l'élève pour confirmer (la ceinture n'est pas modifiée) :`);
    if (!name) return;
    try { await api.post("/ops/academy-structure/validations", { profile_id: profileId, equipment_confirmed: eq, uniform_ok: uni, confirm_name: name.trim() }); toast.success("Entrée de groupe validée par le Kru — ceinture inchangée"); load(); }
    catch (e) { toast.error(e?.response?.status === 428 ? (e.response.data?.detail || "Nom incorrect") : (e?.response?.data?.detail || "Validation impossible")); }
  };
  return (
    <div className="mt-2 rounded-lg border border-sky-500/50 bg-sky-500/10 px-3 py-2 text-xs" data-testid="pilot-group-badge">
      <div className="flex flex-wrap items-center gap-2">
        <span className="px-1.5 py-0.5 rounded bg-sky-600 text-white font-bold">🧪 PILOTE · lecture seule</span>
        {p.group ? <span className="text-sky-100 font-semibold" data-testid="pilot-group-name">Groupe pédagogique : {p.group.name} (niveau {p.group.level})</span> : <span className="text-rose-300">{p.message}</span>}
        {p.validation && <span className="px-1.5 py-0.5 rounded bg-emerald-700 text-white" data-testid="pilot-validated-badge">✔ Validé par {p.validation.validated_by} le {p.validation.validated_at?.slice(0, 10)}</span>}
      </div>
      {p.group && (
        <div className="mt-1 text-zinc-300 space-y-0.5">
          <div data-testid="pilot-group-uniform">Uniforme du groupe : <b>{p.group.uniform}</b> · Offre Académie : {p.group.required_offer}</div>
          {p.validation ? (
            <div className="text-zinc-400">Uniforme requis : {p.checks.uniforme_requis ? "oui" : "non"} · Équipement confirmé : oui · Validation du Kru : oui</div>
          ) : p.can_validate ? (
            <div className="flex flex-wrap items-center gap-3 mt-1" data-testid="pilot-validate-form">
              <label className="flex items-center gap-1 text-zinc-200"><input type="checkbox" checked={eq} onChange={(e) => setEq(e.target.checked)} data-testid="pilot-check-equipment" /> Équipement confirmé</label>
              <label className="flex items-center gap-1 text-zinc-200"><input type="checkbox" checked={uni} onChange={(e) => setUni(e.target.checked)} data-testid="pilot-check-uniform" /> Uniforme conforme au groupe</label>
              <button onClick={validate} disabled={!eq || !uni} className="px-2 py-1 rounded bg-emerald-700 hover:bg-emerald-600 text-white font-semibold disabled:opacity-40" data-testid="pilot-validate-btn">✔ Valider l'entrée de groupe (Kru)</button>
            </div>
          ) : (
            <div className="text-zinc-400">Uniforme requis : {p.checks.uniforme_requis ? "oui" : "non"} · Équipement confirmé : <i>à confirmer</i> · Validation du Kru : <i>à confirmer</i></div>
          )}
          {p.message && <div className="text-amber-200" data-testid="pilot-hinge-message">{p.message}</div>}
          <div className="text-[10px] text-zinc-500">Prévisualisation du référentiel ({p.rule_status}) — la ceinture réelle « {p.belt} » n'est pas modifiée.</div>
        </div>
      )}
    </div>
  );
}
const DISC_DOT = (v) => DISCIPLINES.find((d) => d.value === v)?.dot || "bg-zinc-500";

// Regroupe les techniques suivies par ceinture requise (ordre d'apparition conservé).
function groupByBelt(techs) {
  const map = new Map();
  for (const t of techs) {
    const belt = (t.belt_required || "").trim() || "Sans ceinture";
    if (!map.has(belt)) map.set(belt, []);
    map.get(belt).push(t);
  }
  return [...map.entries()].map(([belt, items]) => ({
    belt, items, slug: belt.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
  }));
}


// Couleur visuelle d'une ceinture (pastille) — partagée avec la carte membre.
const beltHex = beltHexShared;

const STATUS_COLOR = {
  a_decouvrir: "text-zinc-500 bg-zinc-800",
  en_apprentissage: "text-orange-300 bg-orange-900/40",
  en_consolidation: "text-yellow-300 bg-yellow-900/40",
  prete_a_evaluer: "text-cyan-300 bg-cyan-900/40",
  validee: "text-emerald-300 bg-emerald-900/40",
};
const STATUS_LABEL = {
  a_decouvrir: "À découvrir",
  en_apprentissage: "En apprentissage",
  en_consolidation: "En consolidation",
  prete_a_evaluer: "Prête à évaluer",
  validee: "Validée",
};

const EVENT_OPTIONS = [
  { type: "exposure", label: "+1 Cours enseigné", color: "bg-blue-600 hover:bg-blue-500" },
  { type: "repetition", label: "+10 Répétitions", amount: 10, color: "bg-green-600 hover:bg-green-500" },
  { type: "repetition", label: "+50 Reps (bloc)", amount: 50, color: "bg-green-700 hover:bg-green-600" },
  { type: "repetition_resistance", label: "+10 Reps RÉSISTANCE", amount: 10, color: "bg-orange-600 hover:bg-orange-500", competitiveOnly: true },
  { type: "sparring_success", label: "+1 Réussite SPARRING", color: "bg-red-600 hover:bg-red-500", competitiveOnly: true },
  { type: "video_view", label: "+1 Visionnage vidéo", color: "bg-purple-600 hover:bg-purple-500" },
  { type: "active_recall", label: "+1 Rappel actif (quiz)", color: "bg-cyan-600 hover:bg-cyan-500" },
  { type: "auto_video", label: "🎬 Auto-vidéo soumise (+5% bonus)", color: "bg-amber-600 hover:bg-amber-500 text-black" },
];

// Récupère l'échelle officielle de ceintures pour une discipline + âge (source de vérité backend).
function useBeltLadder(discipline, age) {
  const [belts, setBelts] = useState([]);
  useEffect(() => {
    let cancel = false;
    if (!discipline) { setBelts([]); return; }
    (async () => {
      try {
        const q = age !== null && age !== undefined && age !== "" ? `&age=${Number(age)}` : "";
        const { data } = await api.get(`/progression/belt-ladder?discipline=${discipline}${q}`);
        if (!cancel) setBelts(data.belts || []);
      } catch { if (!cancel) setBelts([]); }
    })();
    return () => { cancel = true; };
  }, [discipline, age]);
  return belts;
}

export default function CarnetProgressionModule() {
  const [profiles, setProfiles] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null); // clé du groupe élève
  const [selected, setSelected] = useState(null);               // id du carnet (discipline) affiché
  const [dashboard, setDashboard] = useState(null);
  const [reco, setReco] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newModal, setNewModal] = useState(null); // null | {prefill}
  const [showAddTech, setShowAddTech] = useState(false);
  const [search, setSearch] = useState("");
  const [auditData, setAuditData] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [hasBackup, setHasBackup] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [deleteStudentModal, setDeleteStudentModal] = useState(null); // null | {withAccount, busy}
  const [trash, setTrash] = useState([]);
  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadCoachPdf = async () => {
    if (!selected) return;
    setPdfBusy(true);
    try {
      const r = await api.get(`/progression/profile/${selected}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = `carnet-${(currentStudent?.student_name || "eleve").replace(/\s+/g, "_")}-coach.pdf`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000); toast.success("PDF du carnet téléchargé (signé coach)");
    } catch { toast.error("Export PDF impossible"); }
    setPdfBusy(false);
  };
  const [trashOpen, setTrashOpen] = useState(false);
  const loadTrash = async () => { try { const { data } = await api.get("/progression/trash"); setTrash(data.items || []); } catch { setTrash([]); } };
  const restoreTrash = async (it) => {
    try {
      const { data } = await api.post(`/progression/trash/${it.id}/restore`);
      toast.success(`${data.student} restauré : ${data.carnets_restored} carnet(s), ${data.techniques_restored} technique(s)${data.account_restored ? " · dossier élève réactivé" : ""}${data.portal_unblocked ? " · accès portail rétabli" : ""}`);
      await loadTrash(); await loadProfiles();
    } catch (e) { toast.error(e.response?.data?.detail || "Restauration impossible"); }
  };
  const purgeTrash = async (it) => {
    if (!window.confirm(`Supprimer définitivement ${it.student} de la corbeille ? Irréversible.`)) return;
    try { await api.delete(`/progression/trash/${it.id}`); toast.success("Supprimé définitivement"); await loadTrash(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const [historyData, setHistoryData] = useState([]);
  const [diplomaModal, setDiplomaModal] = useState(null);
  const [convocationModal, setConvocationModal] = useState(null);
  const [completeness, setCompleteness] = useState({});
  const [health, setHealth] = useState(null);
  const [healthDetail, setHealthDetail] = useState(null); // {status, loading, rows}
  const [healthHistory, setHealthHistory] = useState([]);
  const [carnetFilter, setCarnetFilter] = useState("all"); // all | incomplete | empty

  const loadHealth = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/carnet-corrections/health");
      setHealth(data);
    } catch { setHealth(null); }
  }, []);
  useEffect(() => { loadHealth(); loadTrash(); }, [loadHealth]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadHealthHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/carnet-corrections/health/history");
      setHealthHistory(data.snapshots || []);
    } catch { setHealthHistory([]); }
  }, []);
  useEffect(() => { loadHealthHistory(); }, [loadHealthHistory]);

  const openHealthDetail = useCallback(async (status) => {
    setHealthDetail({ status, loading: true, rows: [] });
    try {
      const { data } = await api.get(`/admin/carnet-corrections/health/detail?status=${status}`);
      setHealthDetail({ status, loading: false, rows: data.carnets || [] });
    } catch { setHealthDetail({ status, loading: false, rows: [] }); }
  }, []);

  const loadCompleteness = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/carnet-corrections/completeness");
      setCompleteness(data.incomplete || {});
    } catch { setCompleteness({}); }
  }, []);
  useEffect(() => { loadCompleteness(); }, [loadCompleteness]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/progression/belt-migration/history");
      setHistoryData(data.batches || []);
    } catch { setHistoryData([]); }
  }, []);

  const refreshBackup = useCallback(async () => {
    try {
      const { data } = await api.get("/progression/belt-migration/last-backup");
      setHasBackup(!!data.has_backup);
    } catch { setHasBackup(false); }
  }, []);
  useEffect(() => { refreshBackup(); }, [refreshBackup]);

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/progression/profiles");
      setProfiles(data.profiles || []);
    } catch { toast.error("Erreur chargement profils"); }
    setLoading(false);
  }, []);

  const loadDashboard = useCallback(async (profileId) => {
    if (!profileId) { setDashboard(null); setReco(null); return; }
    try {
      const [d, r] = await Promise.all([
        api.get(`/progression/profile/${profileId}/dashboard`),
        api.get(`/progression/profile/${profileId}/recommendations`),
      ]);
      setDashboard(d.data);
      setReco(r.data);
    } catch { toast.error("Erreur chargement carnet"); }
  }, []);

  useEffect(() => { loadProfiles(); }, [loadProfiles]);
  useEffect(() => { loadDashboard(selected); }, [selected, loadDashboard]);

  // Regroupe les carnets par ÉLÈVE (1 élève = jusqu'à 3 carnets, 1 par discipline).
  const students = useMemo(() => {
    const map = new Map();
    for (const p of profiles) {
      const key = p.student_id || `name:${(p.student_name || "").toLowerCase()}`;
      if (!map.has(key)) {
        map.set(key, { key, student_id: p.student_id, student_name: p.student_name, age: p.age, carnets: {} });
      }
      map.get(key).carnets[p.discipline] = p;
    }
    return Array.from(map.values());
  }, [profiles]);

  const currentStudent = students.find((s) => s.key === selectedStudent) || null;

  // Lien profond depuis Contrats Élèves : ?student=<contract_id>&name=&age=&discipline=
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinked = useRef(false);
  useEffect(() => {
    const sid = searchParams.get("student");
    if (!sid || loading || deepLinked.current) return;
    deepLinked.current = true;
    const found = students.find((s) => s.student_id === sid);
    if (found) {
      setSelectedStudent(found.key);
      const first = DISCIPLINES.map((d) => found.carnets[d.value]).find(Boolean);
      setSelected(first ? first.id : null);
      toast.success(`Carnet de ${found.student_name} ouvert`);
    } else {
      const raw = (searchParams.get("discipline") || "").toLowerCase();
      const disc = raw.includes("bres") || raw.includes("bjj") ? "bjj" : raw.includes("can") || raw.includes("jujutsu") || raw.includes("kids") ? "jujutsu_japonais" : "muay_thai_kickboxing";
      setNewModal({ prefill: { student_id: sid, student_name: searchParams.get("name") || "", age: Number(searchParams.get("age")) || 16, discipline: disc } });
      toast.info("Aucun carnet pour cet élève — crée-le ici (infos préremplies depuis le contrat).");
    }
    setSearchParams({}, { replace: true });
  }, [searchParams, students, loading, setSearchParams]);

  const selectStudent = (s) => {
    setSelectedStudent(s.key);
    const first = DISCIPLINES.map((d) => s.carnets[d.value]).find(Boolean);
    setSelected(first ? first.id : null);
  };

  const toggleAccess = async (revoke) => {
    if (!dashboard?.profile) return;
    const name = dashboard.profile.student_name;
    if (revoke && !window.confirm(`Révoquer l'accès de ${name} ? (abonnement annulé → son carnet/QR sera coupé)`)) return;
    try {
      const ep = revoke ? "/auth/revoke-member-access" : "/auth/restore-member-access";
      await api.post(ep, { profile_id: selected });
      toast.success(revoke ? `Accès révoqué pour ${name}` : `Accès réactivé pour ${name}`);
      loadDashboard(selected);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const deleteStudent = async () => {
    if (!currentStudent || !deleteStudentModal) return;
    setDeleteStudentModal((m) => ({ ...m, busy: true }));
    try {
      const { data } = await api.post("/progression/student/delete", {
        student_id: currentStudent.student_id || null, student_name: currentStudent.student_name || "", with_account: !!deleteStudentModal.withAccount,
      });
      toast.success(`${data.student} : ${data.carnets_removed} carnet(s) et ${data.techniques_removed} technique(s) supprimés${data.account_archived ? " · dossier élève archivé" : ""}${data.portal_blocked ? " · accès portail bloqué" : ""}.`);
      setDeleteStudentModal(null); setSelected(null); setSelectedStudent(null);
      await loadProfiles(); loadTrash();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suppression impossible");
      setDeleteStudentModal((m) => (m ? { ...m, busy: false } : m));
    }
  };

  const deleteProfile = async () => {
    if (!dashboard?.profile) return;
    const name = dashboard.profile.student_name;
    const disc = DISC_LABEL(dashboard.profile.discipline);
    if (!window.confirm(`⚠️ SUPPRIMER DÉFINITIVEMENT le carnet de ${name} (${disc}) ?\n\nCela retire le carnet et toutes ses techniques suivies. Action irréversible.`)) return;
    try {
      const { data } = await api.delete(`/progression/profile/${selected}`);
      toast.success(`Carnet supprimé (${data.techniques_removed} technique(s) retirée(s)).`);
      setSelected(null);
      await loadProfiles();
    } catch (e) { toast.error(e.response?.data?.detail || "Suppression impossible"); }
  };

  const mergeProfile = async () => {
    if (!dashboard?.profile) return;
    const me = dashboard.profile;
    const candidates = profiles.filter((p) => p.id !== me.id && (p.discipline || "").toLowerCase() === (me.discipline || "").toLowerCase());
    if (!candidates.length) { toast.error("Aucun autre carnet dans cette discipline à fusionner."); return; }
    const list = candidates.map((p, i) => `${i + 1}. ${p.student_name} (${p.current_belt || "—"})`).join("\n");
    const pick = window.prompt(`Fusionner un AUTRE carnet ${DISC_LABEL(me.discipline)} DANS celui de ${me.student_name} (ce carnet est conservé).\nTape le numéro du carnet à verser puis supprimer :\n\n${list}`);
    const idx = parseInt(pick, 10) - 1;
    if (Number.isNaN(idx) || !candidates[idx]) return;
    const src = candidates[idx];
    if (!window.confirm(`⚠️ Verser le carnet de « ${src.student_name} » dans « ${me.student_name} » ?\nTechniques déplacées (doublons → meilleur %), puis le carnet source est supprimé. Irréversible.`)) return;
    try {
      const { data } = await api.post(`/progression/profile/${me.id}/merge`, { source_id: src.id });
      toast.success(`Fusion terminée : ${data.moved} technique(s) déplacée(s), ${data.merged} fusionnée(s).`);
      await loadProfiles();
    } catch (e) { toast.error(e.response?.data?.detail || "Fusion impossible"); }
  };

  const changeDiscipline = async (newDisc) => {
    if (!dashboard?.profile || newDisc === dashboard.profile.discipline) return;
    if (!window.confirm(`Changer la discipline de ce carnet vers « ${DISC_LABEL(newDisc)} » ?\n\nLes techniques suivies restent, mais l'échelle de ceintures change.`)) return;
    try {
      await api.patch(`/progression/profile/${selected}`, { discipline: newDisc });
      toast.success("Discipline du carnet changée ✓");
      await loadProfiles();
      loadDashboard(selected);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur changement discipline"); }
  };

  const attachTechniques = async () => {
    if (!dashboard?.profile) return;
    try {
      const { data } = await api.post(`/admin/carnet-corrections/profile/${selected}/attach`);
      if (data.added > 0) toast.success(`🥋 ${data.added} technique(s) rattachée(s) (ceinture ${(data.belts || []).join(", ")})`);
      else if (data.reason) toast.info(data.reason);
      else toast.success("Carnet déjà à jour — aucune technique à ajouter.");
      loadDashboard(selected);
      loadCompleteness();
      loadHealth();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur rattachement"); }
  };

  const printCertificate = async () => {
    if (!dashboard?.profile) return;
    const p = dashboard.profile;
    try {
      const { data } = await api.post("/certificats/generer-manuel", {
        student_name: p.student_name,
        discipline: p.discipline,
        belt_obtained: p.target_belt,
        notes: `Prétest réussi — moyenne ${dashboard.global_average}%`,
        student_contract_id: p.student_id || null,
        cert_type: "promotion",
      });
      const id = data.certificat.id;
      const blob = await api.get(`/certificats/${id}/preview`, { responseType: "blob" });
      window.open(window.URL.createObjectURL(blob.data), "_blank");
      toast.success("Diplôme de ceinture généré 🎓");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur génération diplôme"); }
  };

  const recordEvent = async (techId, eventType, amount, score) => {
    try {
      const body = { event_type: eventType, amount: amount || 1 };
      if (score !== undefined) body.score = score;
      const { data } = await api.post(`/progression/technique/${techId}/event`, body);
      toast.success(`✓ ${data.technique.technique_name}: ${data.technique.current_percent}%`);
      if (data.pretest_unlocked) toast.success("🎉 PRÉTEST DÉBLOQUÉ !", { duration: 8000 });
      loadDashboard(selected);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const submitValidation = async (techId) => {
    const score = parseFloat(prompt("Note du coach (0-100) :"));
    if (!score || isNaN(score)) return;
    await recordEvent(techId, "coach_validation", 1, score);
  };

  const deleteBelt = async (belt, count) => {
    if (!window.confirm(`🗑️ Supprimer la ceinture « ${belt} » de ce carnet ?\n\nLes ${count} technique(s) rattachée(s) à cette ceinture (et leur historique de pratique) seront supprimées définitivement.`)) return;
    try {
      const { data } = await api.delete(`/progression/profile/${selected}/belt/${encodeURIComponent(belt)}`);
      toast.success(`Ceinture ${belt} retirée — ${data.deleted} technique(s) supprimée(s)`);
      loadDashboard(selected);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur suppression ceinture"); }
  };

  const removeTechnique = async (tech) => {
    if (!window.confirm(`Retirer la technique « ${tech.technique_name} » du carnet ?`)) return;
    try {
      await api.delete(`/progression/technique/${tech.id}`);
      toast.success(`Technique retirée : ${tech.technique_name}`);
      loadDashboard(selected);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur suppression technique"); }
  };

  const isCompetitive = dashboard?.profile?.objective_type === "competitif";

  const q = search.trim().toLowerCase();
  const incompleteIdSet = useMemo(() => new Set(health?.incomplete_ids || []), [health]);
  const emptyIdSet = useMemo(() => new Set(health?.empty_ids || []), [health]);
  const filteredStudents = useMemo(() => {
    let list = q ? students.filter((s) => (s.student_name || "").toLowerCase().includes(q)) : students;
    if (carnetFilter === "incomplete") {
      list = list.filter((s) => Object.values(s.carnets).some((c) => incompleteIdSet.has(c.id)));
    } else if (carnetFilter === "empty") {
      list = list.filter((s) => Object.values(s.carnets).some((c) => emptyIdSet.has(c.id)));
    }
    return list;
  }, [q, students, carnetFilter, incompleteIdSet, emptyIdSet]);

  return (
    <div className="space-y-6 p-6" data-testid="carnet-progression-module">
      <PageHeader
        title="Carnet de Progression Premium"
        icon={BookOpen}
        breadcrumbs={[{ label: "Dojo & élèves" }, { label: "Carnet de progression" }]}
        subtitle="Ceintures officielles par âge & discipline · jusqu'à 3 carnets par élève"
        actions={<>
          <button
            data-testid="purge-youtube-carnet-btn"
            onClick={async () => {
              if (!window.confirm("Supprimer TOUTES les vidéos YouTube (liens 404) des carnets ? Le bouton « Voir la vidéo » disparaîtra pour ces techniques.")) return;
              try {
                const r = await api.post("/curriculum/purge-youtube-videos");
                toast.success(`${r.data.techniques_cleared} technique(s) nettoyée(s) · ${r.data.library_removed} vidéo(s) retirée(s).`);
                loadProfiles && loadProfiles();
              } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
            }}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white font-semibold px-4 py-2 rounded-lg"
          >
            <Trash2 className="w-4 h-4" /> Supprimer les vidéos YouTube (404)
          </button>
          <button
            data-testid="new-profile-btn"
            onClick={() => setNewModal({ prefill: null })}
            className="flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-black font-semibold px-4 py-2 rounded-lg"
          >
            <Plus className="w-4 h-4" /> Nouveau carnet élève
          </button>
          <button
            data-testid="bulk-import-btn"
            onClick={async () => {
              if (!window.confirm("Créer/synchroniser un carnet pour TOUS les élèves de la base ?")) return;
              try {
                const { data } = await api.post("/curriculum/bulk-create-carnets");
                toast.success(data.summary);
                loadProfiles();
              } catch { toast.error("Erreur bulk-create"); }
            }}
            className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-amber-300 border border-amber-700 px-4 py-2 rounded-lg"
          >
            <Users className="w-4 h-4" /> Sync tous les élèves
          </button>
          <button
            data-testid="attach-all-btn"
            onClick={async () => {
              try {
                const { data } = await api.post("/admin/carnet-corrections/attach-all?dry_run=true");
                if (!data.techniques_added) { toast.success("Tous les carnets sont déjà complets 🎉"); return; }
                if (!window.confirm(`🥋 Rattacher les techniques à TOUS les carnets\n\n${data.techniques_added} technique(s) seront ajoutées sur ${data.carnets_touched} carnet(s) (sur ${data.total_carnets}).\n\nContinuer ? (additif, aucune progression perdue)`)) return;
                const res = await api.post("/admin/carnet-corrections/attach-all?dry_run=false");
                toast.success(`✅ ${res.data.techniques_added} technique(s) ajoutées sur ${res.data.carnets_touched} carnet(s)`);
                await loadProfiles();
                await loadCompleteness();
                await loadHealth();
                if (selected) loadDashboard(selected);
              } catch (e) { toast.error(e.response?.data?.detail || "Erreur rattachement global"); }
            }}
            className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-emerald-300 border border-emerald-700 px-4 py-2 rounded-lg"
          >
            🥋 Rattacher à TOUS
          </button>
          <button
            data-testid="fill-empty-btn"
            onClick={async () => {
              try {
                const { data } = await api.get("/admin/carnet-corrections/empty-carnets/preview");
                if (!data.empty_count) { toast.success("Aucun carnet vide 🎉"); return; }
                if (!data.fillable_count) { toast.info(`${data.empty_count} carnet(s) vide(s), mais aucune technique au catalogue pour ces disciplines.`); return; }
                const list = data.carnets.filter((c) => c.would_attach)
                  .slice(0, 12).map((c) => `• ${c.student_name} (${c.discipline}) → ${c.would_attach} tech.`).join("\n");
                const ok = window.confirm(
                  `🥋 Remplir les carnets vides\n\n${data.empty_count} carnet(s) vide(s) · ${data.fillable_count} remplissable(s).\n\n${list}${data.fillable_count > 12 ? `\n…+${data.fillable_count - 12} autres` : ""}\n\nRattacher les techniques maintenant ?`
                );
                if (!ok) return;
                const res = await api.post("/admin/carnet-corrections/empty-carnets/apply?confirm=true");
                toast.success(`✅ ${res.data.filled} carnet(s) rempli(s) · ${res.data.techniques_added} techniques ajoutées`);
                loadProfiles();
                loadCompleteness();
                loadHealth();
              } catch (e) { toast.error(e.response?.data?.detail || "Erreur remplissage carnets"); }
            }}
            className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-emerald-300 border border-emerald-700 px-4 py-2 rounded-lg"
          >
            🥋 Remplir les carnets vides
          </button>
          <button
            data-testid="dedupe-btn"
            onClick={async () => {
              try {
                const { data } = await api.post("/progression/dedupe-profiles?dry_run=true");
                if (!data.duplicates_found) { toast.success("Aucun carnet en doublon 🎉"); return; }
                const list = data.would_delete.slice(0, 12).map((d) => `• ${d.name} (${d.discipline}) — ${d.tech_count} tech., ${d.avg}%`).join("\n");
                const ok = window.confirm(`🧹 Nettoyage des carnets doublons\n\n${data.duplicates_found} doublon(s). On GARDE le plus avancé.\n\n${list}\n\nConfirmer la suppression ?`);
                if (!ok) return;
                const res = await api.post("/progression/dedupe-profiles?dry_run=false");
                toast.success(`✅ ${res.data.deleted} doublon(s) supprimé(s)`);
                loadProfiles();
                loadHealth();
              } catch (e) { toast.error(e.response?.data?.detail || "Erreur nettoyage doublons"); }
            }}
            className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-red-300 border border-red-700 px-4 py-2 rounded-lg"
          >
            🧹 Nettoyer doublons
          </button>
          <button
            data-testid="audit-inactive-btn"
            onClick={async () => {
              try {
                const { data } = await api.get("/progression/audit/inactive-carnets");
                setAuditData(data);
                setAuditOpen(true);
              } catch (e) { toast.error(e.response?.data?.detail || "Erreur audit"); }
            }}
            className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-cyan-300 border border-cyan-700 px-4 py-2 rounded-lg"
          >
            🔎 Audit membres actifs
          </button>
          <button
            data-testid="belt-migration-btn"
            onClick={async () => {
              try {
                const { data } = await api.get("/progression/belt-migration/preview");
                if (!data.count) { toast.success("Toutes les ceintures sont déjà conformes 🎉"); return; }
                const list = data.changes.slice(0, 15).map((c) => {
                  const parts = [];
                  if (c.current_changed) parts.push(`actuelle « ${c.current_belt || "—"} » → « ${c.new_current_belt} »`);
                  if (c.target_changed) parts.push(`visée « ${c.target_belt || "—"} » → « ${c.new_target_belt} »`);
                  return `• ${c.student_name} (${DISC_LABEL(c.discipline)}) : ${parts.join(" · ")}`;
                }).join("\n");
                const ok = window.confirm(
                  `🔀 Migration des ceintures vers l'échelle officielle\n\n${data.count} carnet(s) sur ${data.total_carnets} seront mis à jour.\n\n${list}${data.count > 15 ? `\n…+${data.count - 15} autres` : ""}\n\nAppliquer maintenant ? (tu peux réajuster chaque ceinture ensuite)`
                );
                if (!ok) return;
                const res = await api.post("/progression/belt-migration/apply?confirm=true");
                toast.success(`✅ ${res.data.updated} carnet(s) migré(s) vers l'échelle officielle`);
                setHasBackup(true);
                await loadProfiles();
                if (selected) loadDashboard(selected);
              } catch (e) { toast.error(e.response?.data?.detail || "Erreur migration ceintures"); }
            }}
            className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-purple-300 border border-purple-700 px-4 py-2 rounded-lg"
          >
            🔀 Migrer les ceintures
          </button>
          {hasBackup && (
            <button
              data-testid="belt-migration-undo-btn"
              onClick={async () => {
                if (!window.confirm("↩️ Annuler la DERNIÈRE migration ?\n\nLes ceintures reviendront à leur valeur d'avant la migration.")) return;
                try {
                  const res = await api.post("/progression/belt-migration/undo");
                  toast.success(`↩️ ${res.data.restored} carnet(s) restauré(s)`);
                  setHasBackup(false);
                  await loadProfiles();
                  if (selected) loadDashboard(selected);
                } catch (e) { toast.error(e.response?.data?.detail || "Aucune migration à annuler"); }
              }}
              className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-orange-300 border border-orange-700 px-4 py-2 rounded-lg"
            >
              ↩️ Annuler la migration
            </button>
          )}
          <button
            data-testid="pretest-report-btn"
            onClick={async () => {
              try {
                const [{ data: dry }, { data: tpl }] = await Promise.all([
                  api.post("/progression/pretest-report?dry_run=true"),
                  api.get("/progression/convocation-template"),
                ]);
                if (!dry.count) { toast.success("Aucun nouvel élève prêt pour le prétest 👍"); return; }
                setConvocationModal({
                  count: dry.count, students: dry.students || [],
                  email_subject: tpl.email_subject, email_body: tpl.email_body, sms_body: tpl.sms_body, sending: false,
                });
              } catch (e) { toast.error(e.response?.data?.detail || "Erreur alerte prétests"); }
            }}
            className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-emerald-300 border border-emerald-700 px-4 py-2 rounded-lg"
          >
            🔔 Prétests prêts
          </button>
          <button
            data-testid="bulk-diploma-btn"
            onClick={async () => {
              try {
                const { data } = await api.get("/progression/pretest-ready-count");
                if (!data.count) { toast.info("Aucun élève au-dessus du seuil de prétest pour le moment."); return; }
                setDiplomaModal({ students: data.students, selected: new Set(data.students.map((s) => s.profile_id)) });
              } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
            }}
            className="flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-black border border-amber-500 px-4 py-2 rounded-lg font-semibold"
          >
            🎓 Diplômes en lot
          </button>
          <button
            data-testid="migration-history-btn"
            onClick={async () => { await loadHistory(); setHistoryOpen(true); }}
            className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-600 px-4 py-2 rounded-lg"
          >
            📜 Historique migrations
          </button>
          <button
            data-testid="trash-btn"
            onClick={async () => { await loadTrash(); setTrashOpen(true); }}
            className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-600 px-4 py-2 rounded-lg"
          >
            🗑 Corbeille{trash.length > 0 && <span className="ml-1 text-[10px] bg-rose-700 text-white rounded-full px-1.5">{trash.length}</span>}
          </button>
        </>} />

      <BeltLegend />

      {health && health.total > 0 && (
        <div className="mb-4 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="carnet-health-dashboard">
          <button type="button" onClick={() => openHealthDetail("complete")}
            className="text-left rounded-xl border border-zinc-800 bg-zinc-900/60 px-4 py-3 hover:border-zinc-600 transition-colors" data-testid="health-card-total">
            <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-wider">
              <BookOpen className="w-3.5 h-3.5" /> Total carnets
            </div>
            <div className="mt-1 text-2xl font-bold text-zinc-100" data-testid="health-total">{health.total}</div>
          </button>
          <button type="button" onClick={() => openHealthDetail("complete")}
            className="text-left rounded-xl border border-emerald-800/60 bg-emerald-500/10 px-4 py-3 hover:border-emerald-500 transition-colors" data-testid="health-card-complete">
            <div className="flex items-center gap-2 text-emerald-300 text-xs uppercase tracking-wider">
              <CheckCircle2 className="w-3.5 h-3.5" /> Complets
            </div>
            <div className="mt-1 text-2xl font-bold text-emerald-200" data-testid="health-complete">{health.complete}</div>
          </button>
          <button type="button" onClick={() => openHealthDetail("incomplete")}
            className="text-left rounded-xl border border-orange-800/60 bg-orange-500/10 px-4 py-3 hover:border-orange-500 transition-colors" data-testid="health-card-incomplete">
            <div className="flex items-center gap-2 text-orange-300 text-xs uppercase tracking-wider">
              <AlertTriangle className="w-3.5 h-3.5" /> Incomplets
            </div>
            <div className="mt-1 text-2xl font-bold text-orange-200" data-testid="health-incomplete">{health.incomplete}</div>
          </button>
          <button type="button" onClick={() => openHealthDetail("empty")}
            className="text-left rounded-xl border border-zinc-700 bg-zinc-800/40 px-4 py-3 hover:border-zinc-500 transition-colors" data-testid="health-card-empty">
            <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-wider">
              <X className="w-3.5 h-3.5" /> Vides
            </div>
            <div className="mt-1 text-2xl font-bold text-zinc-300" data-testid="health-empty">{health.empty}</div>
            {health.no_catalog > 0 && (
              <div className="mt-0.5 text-[11px] text-zinc-500">dont {health.no_catalog} sans catalogue</div>
            )}
          </button>
        </div>
      )}

      {healthHistory.length >= 2 && (
        <div className="mb-4 rounded-xl border border-zinc-800 bg-zinc-900/40 px-4 py-3" data-testid="carnet-health-chart">
          <div className="flex items-center gap-2 text-sm text-emerald-300 font-semibold mb-2">
            <TrendingUp className="w-4 h-4" /> Évolution des carnets complets (par semaine)
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={healthHistory.map((s) => ({ week: (s.week || "").replace(/^\d+-/, ""), complets: s.complete, incomplets: s.incomplete, vides: s.empty }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="week" tick={{ fill: "#71717a", fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fill: "#71717a", fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", borderRadius: 8, fontSize: 12 }} />
              <Line type="monotone" dataKey="complets" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="incomplets" stroke="#fb923c" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="vides" stroke="#a1a1aa" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <DuplicateCarnetsBanner onMerged={loadProfiles} discLabel={DISC_LABEL} />
      <CarnetEligibilityReport />
      {profiles.length > 0 && (
        Object.keys(completeness).length === 0 ? (
          <div className="mb-4 flex items-center gap-2 text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2" data-testid="carnets-complete-badge">
            <CheckCircle2 className="w-4 h-4" /> Tous les carnets sont complets — aucune technique à rattacher 🎉
          </div>
        ) : (
          <div className="mb-4 flex items-center gap-2 text-sm text-orange-300 bg-orange-500/10 border border-orange-500/30 rounded-lg px-3 py-2" data-testid="carnets-incomplete-badge">
            <AlertTriangle className="w-4 h-4" /> {Object.keys(completeness).length} carnet(s) incomplet(s) — clique « 🥋 Rattacher à TOUS » pour compléter
          </div>
        )
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Liste ÉLÈVES */}
        <aside className="lg:col-span-1 space-y-2" data-testid="profiles-sidebar">
          <h2 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider flex items-center gap-2">
            <Users className="w-4 h-4" /> Élèves ({students.length})
          </h2>
          <div className="relative">
            <input
              data-testid="carnet-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un élève…"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-zinc-900 border border-zinc-800 text-sm outline-none focus:border-amber-500 placeholder:text-zinc-600"
            />
            <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="carnet-quick-filter">
            {[
              { key: "all", label: "Tous", count: null },
              { key: "incomplete", label: "Incomplets", count: health?.incomplete },
              { key: "empty", label: "Vides", count: health?.empty },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setCarnetFilter(f.key)}
                data-testid={`filter-${f.key}`}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                  carnetFilter === f.key
                    ? "bg-amber-600 border-amber-500 text-black font-semibold"
                    : "bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-500"
                }`}
              >
                {f.label}{f.count != null ? ` (${f.count})` : ""}
              </button>
            ))}
          </div>
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin text-amber-400" />
          ) : students.length === 0 ? (
            <p className="text-zinc-500 text-sm">Aucun carnet créé. Clique sur "Nouveau carnet" pour commencer.</p>
          ) : filteredStudents.length === 0 ? (
            <p className="text-zinc-500 text-sm" data-testid="carnet-search-empty">
              {carnetFilter !== "all" && !q
                ? (carnetFilter === "incomplete" ? "Aucun carnet incomplet 🎉" : "Aucun carnet vide 🎉")
                : `Aucun élève ne correspond${q ? ` à « ${search} »` : ""}.`}
            </p>
          ) : (
            filteredStudents.map((s) => {
              const carnets = DISCIPLINES.map((d) => s.carnets[d.value]).filter(Boolean);
              const incomplete = carnets.some((c) => completeness[c.id]);
              return (
                <button
                  key={s.key}
                  onClick={() => selectStudent(s)}
                  data-testid={`student-card-${s.key}`}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    selectedStudent === s.key ? "bg-amber-900/30 border-amber-600" : "bg-zinc-900/50 border-zinc-800 hover:border-zinc-600"
                  }`}
                >
                  <div className="font-semibold truncate flex items-center gap-2">
                    {incomplete && (
                      <span title="Carnet incomplet — techniques du catalogue à rattacher" data-testid={`incomplete-dot-${s.key}`}
                        className="w-2.5 h-2.5 rounded-full bg-orange-500 shrink-0 animate-pulse" />
                    )}
                    <span className="truncate">{s.student_name}</span>
                  </div>
                  <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                    {DISCIPLINES.filter((d) => s.carnets[d.value]).map((d) => (
                      <span key={d.value} title={d.label} className="flex items-center gap-1 text-[10px] text-zinc-400 bg-zinc-950 border border-zinc-800 rounded-full px-2 py-0.5">
                        <span className={`w-2 h-2 rounded-full ${d.dot}`} /> {d.label.split(" ")[0]}
                      </span>
                    ))}
                    <span className="text-[10px] text-zinc-600 ml-auto">{carnets.length}/3 carnet(s)</span>
                  </div>
                </button>
              );
            })
          )}
        </aside>

        {/* Détail ÉLÈVE + ses carnets par discipline */}
        <main className="lg:col-span-3 space-y-4" data-testid="carnet-detail">
          {!currentStudent ? (
            <div className="text-center text-zinc-500 py-20">← Sélectionne un élève pour voir ses carnets</div>
          ) : (
            <>
              {/* En-tête élève + onglets de discipline (jusqu'à 3 carnets) */}
              <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4" data-testid="student-discipline-tabs">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h2 className="text-xl font-bold">{currentStudent.student_name}</h2>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-zinc-500">{currentStudent.age} ans</span>
                    <button onClick={() => setDeleteStudentModal({ withAccount: false, busy: false })} data-testid="delete-student-btn"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-900/60 text-red-300 hover:bg-red-950/40 text-xs">
                      <Trash2 className="w-3.5 h-3.5" /> Supprimer l'élève
                    </button>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  {DISCIPLINES.map((d) => {
                    const carnet = currentStudent.carnets[d.value];
                    if (carnet) {
                      const active = selected === carnet.id;
                      return (
                        <button
                          key={d.value}
                          onClick={() => setSelected(carnet.id)}
                          data-testid={`disc-tab-${d.value}`}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                            active ? "bg-amber-900/40 border-amber-600 text-amber-200" : "bg-zinc-950 border-zinc-800 hover:border-zinc-600 text-zinc-300"
                          }`}
                        >
                          <span className={`w-2 h-2 rounded-full ${d.dot}`} />
                          {d.label}
                          <span title={`Ceinture : ${carnet.current_belt}`} data-testid={`belt-pastille-${d.value}`}
                            className="w-3.5 h-3.5 rounded-full border border-white/30 shrink-0"
                            style={{ backgroundColor: beltHex(carnet.current_belt) }} />
                          <span className="text-xs font-bold text-amber-400">{carnet.global_average}%</span>
                        </button>
                      );
                    }
                    return (
                      <button
                        key={d.value}
                        onClick={() => setNewModal({ prefill: {
                          student_id: currentStudent.student_id || "",
                          student_name: currentStudent.student_name || "",
                          age: currentStudent.age ?? 16,
                          discipline: d.value,
                        } })}
                        data-testid={`disc-add-${d.value}`}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-zinc-700 hover:border-amber-600 text-zinc-500 hover:text-amber-300 text-sm"
                      >
                        <Plus className="w-3.5 h-3.5" /> Ajouter {d.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {!selected ? (
                <div className="text-center text-zinc-500 py-16">Sélectionne ou ajoute un carnet de discipline ci-dessus.</div>
              ) : !dashboard ? (
                <Loader2 className="w-6 h-6 animate-spin text-amber-400 mx-auto" />
              ) : (
                <>
                  {/* Header carnet */}
                  <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 border border-zinc-800 rounded-xl p-5">
                    <div className="flex items-start justify-between flex-wrap gap-3">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`w-2.5 h-2.5 rounded-full ${DISC_DOT(dashboard.profile.discipline)}`} />
                          <select
                            value={dashboard.profile.discipline}
                            onChange={(e) => changeDiscipline(e.target.value)}
                            data-testid="change-discipline-select"
                            className="bg-zinc-950 border border-zinc-700 rounded px-2 py-1 text-sm font-semibold text-amber-200"
                          >
                            {DISCIPLINES.map((d) => (
                              <option
                                key={d.value}
                                value={d.value}
                                disabled={d.value !== dashboard.profile.discipline && !!currentStudent.carnets[d.value]}
                              >
                                {d.label}{d.value !== dashboard.profile.discipline && currentStudent.carnets[d.value] ? " (existe déjà)" : ""}
                              </option>
                            ))}
                          </select>
                        </div>
                        <p className="text-zinc-400 text-sm mt-2">
                          <span className="text-amber-300">{dashboard.profile.current_belt}</span>
                          {" → "}
                          <span className="text-amber-300">{dashboard.profile.target_belt}</span>
                          {" · "}{dashboard.profile.age} ans ({dashboard.profile.age_group.replace("_", "-")})
                        </p>
                        <PilotGroupBadge profileId={dashboard.profile.id} belt={dashboard.profile.current_belt} />
                      </div>
                      <div className="text-right">
                        <div className="text-4xl font-bold text-amber-400">{dashboard.global_average}%</div>
                        <div className="text-xs text-zinc-500">Moyenne globale · seuil {dashboard.pretest_threshold}%</div>
                        {dashboard.profile.active === false ? (
                          <button data-testid="restore-access-btn" onClick={() => toggleAccess(false)}
                            className="mt-2 px-3 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-bold">
                            ✓ Réactiver l'accès
                          </button>
                        ) : (
                          <button data-testid="revoke-access-btn" onClick={() => toggleAccess(true)}
                            className="mt-2 px-3 py-1.5 rounded-lg bg-red-900/70 hover:bg-red-800 text-red-100 text-xs font-bold border border-red-700/50">
                            🚫 Révoquer l'accès
                          </button>
                        )}
                        <button data-testid="merge-carnet-btn" onClick={mergeProfile} title="Fusionner 2 carnets du même élève (doublon)"
                          className="mt-2 ml-2 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-amber-700 text-zinc-300 hover:text-white text-xs font-bold border border-zinc-700">
                          🔗 Fusionner un doublon
                        </button>
                        <button data-testid="delete-carnet-btn" onClick={deleteProfile}
                          className="mt-2 ml-2 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-red-800 text-zinc-300 hover:text-white text-xs font-bold border border-zinc-700">
                          🗑️ Supprimer ce carnet
                        </button>
                        <button data-testid="coach-pdf-btn" onClick={downloadCoachPdf} disabled={pdfBusy} title="Export PDF vue coach (tous les %, signature du coach)"
                          className="mt-2 ml-2 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-emerald-700 text-zinc-300 hover:text-white text-xs font-bold border border-zinc-700 disabled:opacity-50">
                          📄 {pdfBusy ? "PDF…" : "Exporter en PDF (signé coach)"}
                        </button>
                      </div>
                    </div>

                    <BeltEditor
                      profile={dashboard.profile}
                      onSaved={() => { loadProfiles(); loadDashboard(selected); }}
                    />

                    {dashboard.pretest_unlocked && (
                      <div className="mt-4 bg-emerald-900/40 border border-emerald-600 rounded-lg p-3 flex items-center gap-3 flex-wrap" data-testid="pretest-unlocked-banner">
                        <Trophy className="w-6 h-6 text-emerald-400" />
                        <div className="flex-1 min-w-[180px]">
                          <div className="font-bold text-emerald-300">🎉 Prétest 45 min débloqué !</div>
                          <div className="text-xs text-emerald-200/80">Ta structure est solide. Présente-toi au dojo pour la convocation.</div>
                        </div>
                        <button onClick={printCertificate} data-testid="belt-certificate-btn"
                          className="px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-black text-xs font-bold">
                          🎓 Diplôme de ceinture
                        </button>
                      </div>
                    )}

                    {!dashboard.display_rules.show_percent_to_student && (
                      <div className="mt-3 text-xs text-amber-300/80 italic flex items-center gap-1">
                        <Star className="w-3 h-3" /> Affichage élève : étoiles & badges uniquement (règle 3-7 ans)
                      </div>
                    )}

                    <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center gap-3 flex-wrap" data-testid="qr-section">
                      <button
                        onClick={async () => {
                          try {
                            const { data } = await api.post(`/progression/${dashboard.profile.id}/qr-token?valid_days=365`);
                            await navigator.clipboard.writeText(data.public_url);
                            toast.success("Lien copié ! Ouvre-le sur ton mobile.");
                            window.open(data.public_url, "_blank");
                          } catch { toast.error("Erreur génération QR"); }
                        }}
                        className="px-3 py-2 bg-amber-600 hover:bg-amber-500 text-zinc-950 rounded-lg text-sm font-medium"
                        data-testid="qr-mobile-btn"
                      >
                        📱 Vue mobile QR
                      </button>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/progression/${dashboard.profile.id}/qr.png?valid_days=365`}
                        target="_blank" rel="noreferrer"
                        className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-lg text-sm"
                        data-testid="qr-png-btn"
                      >
                        🖼️ Télécharger QR PNG
                      </a>
                      <span className="text-xs text-zinc-500">QR signé · validité 365j</span>
                    </div>

                    <MemberLinkSection profileId={dashboard.profile.id} />
                  </div>

                  {/* Techniques */}
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold flex items-center gap-2">
                        <Activity className="w-5 h-5 text-amber-400" /> Techniques suivies ({dashboard.techniques.length})
                      </h3>
                      <div className="flex items-center gap-3">
                        <button onClick={attachTechniques} data-testid="attach-techniques-btn"
                          className="flex items-center gap-1 text-emerald-300 hover:text-emerald-200 text-sm">
                          🥋 Rattacher les techniques du catalogue
                        </button>
                        <button onClick={() => setShowAddTech(true)} data-testid="add-technique-btn"
                          className="flex items-center gap-1 text-amber-300 hover:text-amber-200 text-sm">
                          <Plus className="w-4 h-4" /> Ajouter une technique
                        </button>
                      </div>
                    </div>
                    {dashboard.techniques.length === 0 ? (
                      <p className="text-zinc-500 text-sm">Aucune technique suivie. Ajoute la première !</p>
                    ) : (
                      <div className="space-y-5">
                        {groupByBelt(dashboard.techniques).map((g) => (
                          <div key={g.belt} data-testid={`belt-group-${g.slug}`}>
                            <div className="flex items-center justify-between gap-3 mb-2 px-1">
                              <div className="flex items-center gap-2 text-sm font-semibold text-zinc-200">
                                <span className="w-3 h-3 rounded-full border border-white/30 shrink-0" style={{ backgroundColor: beltHex(g.belt) }} />
                                Ceinture {g.belt} <span className="text-zinc-500 font-normal">· {g.items.length} technique{g.items.length > 1 ? "s" : ""}</span>
                              </div>
                              <button onClick={() => deleteBelt(g.belt, g.items.length)} data-testid={`delete-belt-btn-${g.slug}`}
                                className="text-xs px-2.5 py-1 rounded-lg bg-zinc-800 hover:bg-red-800 text-zinc-400 hover:text-white border border-zinc-700 transition-colors">
                                🗑️ Supprimer cette ceinture
                              </button>
                            </div>
                            <div className="space-y-3">
                              {g.items.map((t) => (
                                <TechniqueRow key={t.id} tech={t} isCompetitive={isCompetitive}
                                  onEvent={recordEvent} onValidate={() => submitValidation(t.id)} onRemove={() => removeTechnique(t)} />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Recommandations */}
                  {reco && (
                    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5" data-testid="recommendations-card">
                      <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                        <Sparkles className="w-5 h-5 text-amber-400" /> Recommandations de la semaine
                      </h3>
                      {reco.weekly_actions?.length > 0 && (
                        <ul className="space-y-2 mb-4">
                          {reco.weekly_actions.map((a, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm">
                              <ChevronRight className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                              <div><span className="font-semibold text-amber-300">{a.action}</span>{" "}<span className="text-zinc-300">— {a.detail}</span></div>
                            </li>
                          ))}
                        </ul>
                      )}
                      {reco.ready_to_evaluate?.length > 0 && (
                        <div className="bg-emerald-900/20 border border-emerald-700/40 rounded p-3 mb-3">
                          <div className="text-emerald-300 text-sm font-semibold mb-1">✓ Prêtes à évaluer ({reco.ready_to_evaluate.length})</div>
                          <ul className="text-xs text-emerald-200/80 space-y-0.5">
                            {reco.ready_to_evaluate.map((t, i) => (<li key={i}>• {t.name} ({t.percent}%)</li>))}
                          </ul>
                        </div>
                      )}
                      {reco.not_reviewed_14_days?.length > 0 && (
                        <div className="bg-orange-900/20 border border-orange-700/40 rounded p-3 mb-3">
                          <div className="text-orange-300 text-sm font-semibold mb-1 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Non revues depuis +14 jours</div>
                          <ul className="text-xs text-orange-200/80 space-y-0.5">
                            {reco.not_reviewed_14_days.map((t, i) => (<li key={i}>• {t.technique} — {t.days_since} jours ({t.percent}%)</li>))}
                          </ul>
                        </div>
                      )}
                      {reco.competitive_plan && (
                        <div className="bg-red-900/20 border border-red-700/40 rounded p-3">
                          <div className="text-red-300 text-sm font-semibold mb-2">⚔️ Plan compétitif</div>
                          <div className="text-xs space-y-2">
                            <div><strong className="text-emerald-300">Game plan (forces):</strong>{" "}{reco.competitive_plan.game_plan_strengths.map(s => `${s.name} (${s.percent}%)`).join(", ") || "—"}</div>
                            <div><strong className="text-orange-300">À drill (faibles):</strong>{" "}{reco.competitive_plan.weak_spots_to_drill.map(s => `${s.name} (${s.percent}%)`).join(", ") || "—"}</div>
                            <p className="text-zinc-300 italic">{reco.competitive_plan.tapering_advice}</p>
                            <p className="text-zinc-400">{reco.competitive_plan.video_analysis_weekly}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </main>
      </div>

      {newModal && (
        <NewProfileModal
          prefill={newModal.prefill}
          onClose={() => setNewModal(null)}
          onCreated={async (id, key) => { setNewModal(null); await loadProfiles(); if (key) setSelectedStudent(key); setSelected(id); }}
        />
      )}
      {showAddTech && selected && dashboard?.profile && (
        <AddTechniqueModal
          profileId={selected}
          discipline={dashboard.profile.discipline}
          age={dashboard.profile.age}
          onClose={() => setShowAddTech(false)}
          onAdded={() => { setShowAddTech(false); loadDashboard(selected); }}
        />
      )}
      {auditOpen && auditData && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setAuditOpen(false)} data-testid="audit-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col rounded-2xl border border-zinc-700 bg-zinc-950">
            <div className="p-5 border-b border-zinc-800">
              <h3 className="text-lg font-bold text-cyan-300">🔎 Audit — Carnets sans membre actif</h3>
              <p className="text-xs text-zinc-400 mt-1">Lecture seule · Validez avant d'archiver. Aucune suppression automatique.</p>
              <div className="flex gap-4 mt-3 text-sm">
                <span className="text-zinc-300">Total : <b>{auditData.total_carnets}</b></span>
                <span className="text-emerald-400">Membres actifs : <b>{auditData.actifs}</b></span>
                <span className="text-amber-400">À revoir : <b>{auditData.inactifs_count}</b></span>
              </div>
            </div>
            <div className="overflow-y-auto p-5 space-y-2">
              {auditData.inactifs_count === 0 ? (
                <p className="text-emerald-300 text-sm">✅ Tous les carnets correspondent à un membre actif.</p>
              ) : (
                auditData.inactifs.map((x) => (
                  <div key={x.profile_id} data-testid={`audit-row-${x.profile_id}`} className="flex items-center justify-between bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2">
                    <div>
                      <div className="text-sm text-zinc-100">{x.student_name}</div>
                      <div className="text-[11px] text-zinc-500">{x.discipline || "—"}</div>
                    </div>
                    <span className="text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">{x.raison}</span>
                  </div>
                ))
              )}
            </div>
            <div className="p-4 border-t border-zinc-800 flex justify-end">
              <button onClick={() => setAuditOpen(false)} data-testid="audit-close" className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm">Fermer</button>
            </div>
          </div>
        </div>
      )}

      {historyOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setHistoryOpen(false)} data-testid="migration-history-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col rounded-2xl border border-zinc-700 bg-zinc-950">
            <div className="p-5 border-b border-zinc-800">
              <h3 className="text-lg font-bold text-purple-300">📜 Historique des migrations de ceintures</h3>
              <p className="text-xs text-zinc-400 mt-1">Chaque lot peut être annulé individuellement (restaure les ceintures d'origine).</p>
            </div>
            <div className="overflow-y-auto p-5 space-y-2">
              {historyData.length === 0 ? (
                <p className="text-zinc-500 text-sm">Aucune migration effectuée pour le moment.</p>
              ) : (
                historyData.map((b) => (
                  <div key={b.id} data-testid={`history-row-${b.id}`} className="flex items-center justify-between bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 gap-3">
                    <div>
                      <div className="text-sm text-zinc-100">{new Date(b.created_at).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" })}</div>
                      <div className="text-[11px] text-zinc-500">{b.count} carnet(s) · lot {b.id.slice(0, 8)}</div>
                    </div>
                    {b.undone ? (
                      <span className="text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-zinc-700/40 text-zinc-400 border border-zinc-600">Annulé</span>
                    ) : (
                      <button
                        data-testid={`history-undo-${b.id}`}
                        onClick={async () => {
                          if (!window.confirm(`↩️ Annuler ce lot (${b.count} carnet(s)) ? Les ceintures d'origine seront restaurées.`)) return;
                          try {
                            const res = await api.post(`/progression/belt-migration/undo/${b.id}`);
                            toast.success(`↩️ ${res.data.restored} carnet(s) restauré(s)`);
                            await loadHistory();
                            await refreshBackup();
                            await loadProfiles();
                            if (selected) loadDashboard(selected);
                          } catch (e) { toast.error(e.response?.data?.detail || "Erreur annulation"); }
                        }}
                        className="text-xs px-3 py-1.5 rounded-lg bg-orange-900/60 hover:bg-orange-800 text-orange-100 border border-orange-700/50 font-semibold shrink-0">
                        ↩️ Annuler ce lot
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
            <div className="p-4 border-t border-zinc-800 flex justify-end">
              <button onClick={() => setHistoryOpen(false)} data-testid="history-close" className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm">Fermer</button>
            </div>
          </div>
        </div>
      )}
      {diplomaModal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setDiplomaModal(null)} data-testid="diploma-select-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col rounded-2xl border border-zinc-700 bg-zinc-950">
            <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-amber-300">🎓 Diplômes en lot — sélection</h3>
                <p className="text-xs text-zinc-400 mt-1">Coche les élèves à diplômer. Un PDF combiné imprimable sera généré.</p>
              </div>
              <button
                data-testid="diploma-select-all"
                onClick={() => setDiplomaModal((m) => ({ ...m, selected: m.selected.size === m.students.length ? new Set() : new Set(m.students.map((s) => s.profile_id)) }))}
                className="text-xs text-amber-300 hover:text-amber-200 underline">
                {diplomaModal.selected.size === diplomaModal.students.length ? "Tout décocher" : "Tout cocher"}
              </button>
            </div>
            <div className="overflow-y-auto p-5 space-y-1.5">
              {diplomaModal.students.map((s) => {
                const on = diplomaModal.selected.has(s.profile_id);
                return (
                  <label key={s.profile_id} data-testid={`diploma-pick-${s.profile_id}`}
                    className="flex items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 cursor-pointer hover:border-zinc-600">
                    <input type="checkbox" checked={on}
                      onChange={() => setDiplomaModal((m) => {
                        const sel = new Set(m.selected);
                        if (sel.has(s.profile_id)) sel.delete(s.profile_id); else sel.add(s.profile_id);
                        return { ...m, selected: sel };
                      })}
                      className="w-4 h-4 accent-amber-500" />
                    <span className="w-3 h-3 rounded-full border border-white/30 shrink-0" style={{ backgroundColor: beltHex(s.target_belt) }} />
                    <span className="text-sm text-zinc-100 flex-1">{s.name}</span>
                    <span className="text-xs text-zinc-500">{DISC_LABEL(s.discipline)} · {s.target_belt}</span>
                  </label>
                );
              })}
            </div>
            <div className="p-4 border-t border-zinc-800 flex items-center justify-between">
              <span className="text-xs text-zinc-500">{diplomaModal.selected.size} / {diplomaModal.students.length} sélectionné(s)</span>
              <div className="flex gap-2">
                <button onClick={() => setDiplomaModal(null)} className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm">Annuler</button>
                <button
                  data-testid="diploma-generate-btn"
                  disabled={diplomaModal.selected.size === 0}
                  onClick={async () => {
                    try {
                      const ids = Array.from(diplomaModal.selected);
                      const res = await api.post("/certificats/generer-lot", { profile_ids: ids });
                      toast.success(`🎓 ${res.data.count} diplôme(s) générés`);
                      const lotId = res.data.combined_url.split("/lot/")[1].split("/pdf")[0];
                      const blob = await api.get(`/certificats/lot/${lotId}/pdf`, { responseType: "blob" });
                      window.open(window.URL.createObjectURL(blob.data), "_blank");
                      setDiplomaModal(null);
                    } catch (e) { toast.error(e.response?.data?.detail || "Erreur génération"); }
                  }}
                  className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-black text-sm font-bold disabled:opacity-40">
                  🎓 Générer le PDF combiné
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {convocationModal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setConvocationModal(null)} data-testid="convocation-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-zinc-700 bg-zinc-950 p-5 space-y-3">
            <div>
              <h3 className="text-lg font-bold text-emerald-300">🔔 Convocation prétest — aperçu</h3>
              <p className="text-xs text-zinc-400 mt-1">
                {convocationModal.count} élève(s) prêt(s). Courriel + SMS envoyés à l'élève (si connus) et alerte au Kru. Variables : <code className="text-amber-300">{"{name}"}</code>, <code className="text-amber-300">{"{belt}"}</code>.
              </p>
            </div>
            <label className="block">
              <span className="text-xs text-zinc-400">Objet du courriel</span>
              <input data-testid="conv-email-subject" value={convocationModal.email_subject}
                onChange={(e) => setConvocationModal((m) => ({ ...m, email_subject: e.target.value }))}
                className="mt-1 w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-xs text-zinc-400">Message courriel</span>
              <textarea data-testid="conv-email-body" rows={4} value={convocationModal.email_body}
                onChange={(e) => setConvocationModal((m) => ({ ...m, email_body: e.target.value }))}
                className="mt-1 w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-xs text-zinc-400">SMS (max ~300 car.)</span>
              <textarea data-testid="conv-sms-body" rows={3} value={convocationModal.sms_body}
                onChange={(e) => setConvocationModal((m) => ({ ...m, sms_body: e.target.value }))}
                className="mt-1 w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm" />
            </label>
            <div className="flex items-center justify-between gap-2 pt-1">
              <button data-testid="conv-save-template"
                onClick={async () => {
                  try {
                    await api.put("/progression/convocation-template", {
                      email_subject: convocationModal.email_subject, email_body: convocationModal.email_body, sms_body: convocationModal.sms_body });
                    toast.success("Modèle enregistré ✓");
                  } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                }}
                className="text-xs text-zinc-400 hover:text-emerald-300 underline">💾 Enregistrer le modèle</button>
              <div className="flex gap-2">
                <button onClick={() => setConvocationModal(null)} className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm">Annuler</button>
                <button data-testid="conv-send-btn" disabled={convocationModal.sending}
                  onClick={async () => {
                    setConvocationModal((m) => ({ ...m, sending: true }));
                    try {
                      await api.put("/progression/convocation-template", {
                        email_subject: convocationModal.email_subject, email_body: convocationModal.email_body, sms_body: convocationModal.sms_body });
                      const res = await api.post("/progression/pretest-report?dry_run=false");
                      toast.success(`✉️ Convocations envoyées — Kru: ${res.data.emailed ? "oui" : "non"}, élèves courriel: ${res.data.students_emailed || 0}, SMS: ${res.data.students_texted || 0}`);
                      setConvocationModal(null);
                    } catch (e) { toast.error(e.response?.data?.detail || "Erreur envoi"); setConvocationModal((m) => ({ ...m, sending: false })); }
                  }}
                  className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold disabled:opacity-40">
                  🔔 Envoyer les convocations
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {healthDetail && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setHealthDetail(null)} data-testid="health-detail-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
              <h2 className="text-lg font-bold flex items-center gap-2">
                {healthDetail.status === "complete" && <><CheckCircle2 className="w-5 h-5 text-emerald-400" /> Carnets complets</>}
                {healthDetail.status === "incomplete" && <><AlertTriangle className="w-5 h-5 text-orange-400" /> Carnets incomplets</>}
                {healthDetail.status === "empty" && <><X className="w-5 h-5 text-zinc-400" /> Carnets vides</>}
                {healthDetail.status === "no_catalog" && <><AlertTriangle className="w-5 h-5 text-zinc-400" /> Sans catalogue</>}
              </h2>
              <button onClick={() => setHealthDetail(null)} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="overflow-y-auto p-4 space-y-2">
              {healthDetail.loading ? (
                <Loader2 className="w-6 h-6 animate-spin text-amber-400 mx-auto my-8" />
              ) : healthDetail.rows.length === 0 ? (
                <p className="text-zinc-500 text-sm text-center py-8">Aucun carnet dans cette catégorie 🎉</p>
              ) : (
                healthDetail.rows.map((r) => (
                  <button
                    key={r.profile_id}
                    data-testid={`health-detail-row-${r.profile_id}`}
                    onClick={() => {
                      const stu = students.find((s) => Object.values(s.carnets).some((c) => c.id === r.profile_id));
                      if (stu) { setSelectedStudent(stu.key); setSelected(r.profile_id); }
                      setHealthDetail(null);
                    }}
                    className="w-full text-left flex items-center gap-3 p-3 rounded-lg bg-zinc-950 border border-zinc-800 hover:border-amber-600 transition-colors"
                  >
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: beltHex(r.current_belt) }} />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{r.student_name}</div>
                      <div className="text-[11px] text-zinc-500">{DISC_LABEL(r.discipline)} · {r.current_belt || "—"}</div>
                    </div>
                    {healthDetail.status === "incomplete" && (
                      <span className="text-xs text-orange-300 shrink-0">{r.missing} manquante(s)</span>
                    )}
                    {healthDetail.status === "empty" && (
                      <span className="text-xs text-zinc-500 shrink-0">0 technique</span>
                    )}
                    <ChevronRight className="w-4 h-4 text-zinc-600 shrink-0" />
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
      {trashOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setTrashOpen(false)} data-testid="trash-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col rounded-2xl border border-zinc-700 bg-zinc-950">
            <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-rose-300">🗑 Corbeille des élèves</h3>
                <p className="text-xs text-zinc-400 mt-1">Les élèves et carnets supprimés sont conservés 30 jours, puis effacés automatiquement. « Restaurer » remet tout en place (carnets, techniques, dossier élève, accès portail).</p>
              </div>
              <button onClick={() => setTrashOpen(false)} className="text-zinc-400 hover:text-white" data-testid="trash-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 overflow-auto space-y-2">
              {trash.length === 0 && <p className="text-sm text-zinc-500" data-testid="trash-empty">La corbeille est vide.</p>}
              {trash.map((it) => {
                const days = Math.max(0, Math.ceil((new Date(it.expires_at) - Date.now()) / 86400000));
                return (
                  <div key={it.id} className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-3 flex items-start justify-between gap-3" data-testid={`trash-item-${it.id}`}>
                    <div className="min-w-0">
                      <div className="font-semibold text-zinc-100 truncate">{it.student}</div>
                      <div className="text-xs text-zinc-400 mt-0.5">{it.reason} · {(it.carnets || []).map((c) => c.discipline).join(", ") || "—"}{it.contract_id && " · dossier élève archivé"}</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">Supprimé le {String(it.deleted_at).slice(0, 10)} · <span className={days <= 5 ? "text-rose-300" : ""}>effacement dans {days} jour{days > 1 ? "s" : ""}</span></div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => restoreTrash(it)} className="px-3 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-bold" data-testid={`trash-restore-${it.id}`}>↩ Restaurer</button>
                      <button onClick={() => purgeTrash(it)} className="px-2 py-1.5 rounded-lg border border-zinc-700 text-zinc-400 hover:text-rose-300 text-xs" data-testid={`trash-purge-${it.id}`}>Effacer</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {deleteStudentModal && currentStudent && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => !deleteStudentModal.busy && setDeleteStudentModal(null)} data-testid="delete-student-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl border border-red-900/60 bg-zinc-950 p-5">
            <h3 className="text-lg font-bold text-red-300 flex items-center gap-2"><Trash2 className="w-5 h-5" /> Supprimer l'élève {currentStudent.student_name} ?</h3>
            <p className="text-sm text-zinc-400 mt-2">Ses <b>{Object.keys(currentStudent.carnets).length} carnet(s)</b> de progression et toutes les techniques suivies seront déplacés dans la <b>Corbeille</b> (restaurable pendant 30 jours).</p>
            <label className="mt-4 flex items-start gap-2 text-sm text-zinc-200 cursor-pointer">
              <input type="checkbox" className="mt-0.5" checked={!!deleteStudentModal.withAccount} disabled={!currentStudent.student_id}
                onChange={(e) => setDeleteStudentModal((m) => ({ ...m, withAccount: e.target.checked }))} data-testid="delete-student-with-account" />
              <span><b>Supprimer aussi le dossier élève</b><br /><span className="text-xs text-zinc-500">Contrat archivé (masqué des listes) et accès au portail membre bloqué. Paiements Stripe et historique comptable conservés. Refusé si un abonnement Stripe est encore actif.{!currentStudent.student_id && " — Indisponible : cet élève n'est lié à aucun contrat."}</span></span>
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setDeleteStudentModal(null)} disabled={deleteStudentModal.busy} className="px-4 py-2 rounded-lg border border-zinc-700 text-zinc-300 text-sm" data-testid="delete-student-cancel">Annuler</button>
              <button onClick={deleteStudent} disabled={deleteStudentModal.busy} className="px-4 py-2 rounded-lg bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white text-sm font-bold" data-testid="delete-student-confirm">
                {deleteStudentModal.busy ? "Suppression…" : deleteStudentModal.withAccount ? "Supprimer carnets + dossier" : "Supprimer les carnets"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const BELT_LADDERS = [
  { title: "Kickboxing / Muay Thaï — Adultes (18 ans +)", belts: ["Jaune", "Orange", "Rouge", "Verte", "Bleue", "Mauve", "Brune", "Noire"] },
  { title: "Kickboxing / Muay Thaï — Juniors (7-17 ans)", belts: ["Blanche/Jaune", "Jaune", "Jaune/Orange", "Orange", "Orange/Rouge", "Rouge", "Rouge/Verte", "Verte", "Verte/Bleue", "Bleue", "Bleue/Avancé", "Mauve/Avancé", "Mauve", "Brune/Avancé", "Brune", "Noire/Avancé"] },
  { title: "Autres disciplines — Adultes", belts: ["Jaune", "Orange", "Verte", "Bleue", "Mauve", "Brune", "Noire"] },
  { title: "Jiu-Jitsu Brésilien (BJJ)", belts: ["Bleu", "Mauve", "Brune", "Noire"] },
];

function BeltLegend() {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl px-4 py-3 mb-4" data-testid="belt-legend">
      <button onClick={() => setOpen(!open)} data-testid="belt-legend-toggle"
        className="flex items-center gap-2 text-sm text-amber-300 font-semibold">
        🎨 Légende des couleurs de ceinture <span className="text-zinc-500">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4">
          {BELT_LADDERS.map((l) => (
            <div key={l.title}>
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">{l.title}</div>
              <div className="flex flex-wrap gap-1.5">
                {l.belts.map((b) => (
                  <span key={b} className="inline-flex items-center gap-1 text-[11px] text-zinc-300 bg-zinc-950 border border-zinc-800 rounded-full px-2 py-0.5">
                    <span className="w-2.5 h-2.5 rounded-full border border-white/20 shrink-0" style={{ backgroundColor: beltHex(b) }} /> {b}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Édition rapide des ceintures (actuelle & visée) depuis les échelles officielles.
function BeltEditor({ profile, onSaved }) {
  const belts = useBeltLadder(profile.discipline, profile.age);
  const [cur, setCur] = useState(profile.current_belt);
  const [tar, setTar] = useState(profile.target_belt);
  const [busy, setBusy] = useState(false);
  const [celebrate, setCelebrate] = useState(true);

  useEffect(() => { setCur(profile.current_belt); setTar(profile.target_belt); }, [profile.id, profile.current_belt, profile.target_belt]);

  const dirty = cur !== profile.current_belt || tar !== profile.target_belt;
  const beltChanged = cur !== profile.current_belt;
  const save = async () => {
    if (beltChanged && celebrate && !window.confirm(`🎉 Décerner la ceinture « ${cur} » à ${profile.student_name} ?\n\nSa carte de membre passera à la nouvelle couleur et un SMS de félicitations sera envoyé (élève ou parent).`)) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/progression/profile/${profile.id}`, { current_belt: cur, target_belt: tar, celebrate_sms: celebrate });
      toast.success("Ceintures mises à jour ✓");
      if (data.notification) toast.info(data.notification, { duration: 9000 });
      const c = data.celebration;
      if (c) {
        if (c.sms_sent) toast.success(`🎉 Ceinture ${c.belt} décernée — SMS de félicitations envoyé au ${c.phone}`, { duration: 8000 });
        else if (celebrate) toast.warning(`Ceinture ${c.belt} décernée — SMS non envoyé (${c.error || "aucun téléphone"})`, { duration: 8000 });
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };

  return (
    <div className="mt-4 pt-4 border-t border-zinc-800" data-testid="belt-editor">
      <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-2">CEINTURES OFFICIELLES</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
        <BeltSelect label="Ceinture actuelle" value={cur} onChange={setCur} belts={belts} testId="belt-current-select" />
        <BeltSelect label="Ceinture visée" value={tar} onChange={setTar} belts={belts} testId="belt-target-select" />
        <button onClick={save} disabled={!dirty || busy} data-testid="belt-save-btn"
          className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-black rounded-lg text-sm font-semibold disabled:opacity-40">
          {busy ? "…" : beltChanged ? "🎉 Décerner la ceinture" : "Enregistrer"}
        </button>
      </div>
      {beltChanged && (
        <label className="mt-3 flex items-center gap-2 text-xs text-zinc-300 cursor-pointer" data-testid="belt-celebrate-label">
          <input type="checkbox" checked={celebrate} onChange={(e) => setCelebrate(e.target.checked)} data-testid="belt-celebrate-checkbox" className="accent-amber-500" />
          🎉 Envoyer un SMS de félicitations (nouvelle couleur + lien de la carte) à l'élève / au parent
        </label>
      )}
    </div>
  );
}

function TechniqueRow({ tech, isCompetitive, onEvent, onValidate, onRemove }) {
  const [expanded, setExpanded] = useState(false);
  const c = tech.counters || {};
  return (
    <div className="bg-zinc-950/50 border border-zinc-800 rounded-lg p-3" data-testid={`tech-row-${tech.id}`}>
      <div className="flex items-center justify-between gap-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex-grow min-w-0">
          <div className="font-medium truncate">{tech.technique_name}</div>
          <div className="text-xs text-zinc-500">{tech.category} · {tech.belt_required}</div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLOR[tech.status]}`}>{STATUS_LABEL[tech.status]}</span>
          <div className="w-24 bg-zinc-800 rounded-full h-2 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-amber-600 to-amber-400 transition-all" style={{ width: `${tech.current_percent}%` }} />
          </div>
          <span className="text-sm font-bold text-amber-400 w-12 text-right">{tech.current_percent}%</span>
          {onRemove && (
            <button onClick={(e) => { e.stopPropagation(); onRemove(); }} data-testid={`remove-tech-btn-${tech.id}`}
              title="Retirer cette technique du carnet"
              className="w-6 h-6 rounded-md text-zinc-500 hover:text-white hover:bg-red-800 text-sm leading-none transition-colors">✕</button>
          )}
        </div>
      </div>
      {expanded && (
        <div className="mt-3 pt-3 border-t border-zinc-800 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-zinc-400">
            <div>Cours: <strong className="text-zinc-200">{c.exposures || 0}</strong></div>
            <div>Reps: <strong className="text-zinc-200">{c.repetitions || 0}</strong></div>
            <div>Vidéos: <strong className="text-zinc-200">{c.video_views || 0}</strong></div>
            <div>Validations: <strong className="text-zinc-200">{(c.coach_validations || []).length}</strong></div>
            {isCompetitive && (<>
              <div>Résistance: <strong className="text-zinc-200">{c.repetitions_resistance || 0}</strong></div>
              <div>Sparring: <strong className="text-zinc-200">{c.sparring_successes || 0}</strong></div>
            </>)}
          </div>
          <div className="flex flex-wrap gap-2">
            {EVENT_OPTIONS.filter(e => !e.competitiveOnly || isCompetitive).map((evt, i) => (
              <button key={i} onClick={(e) => { e.stopPropagation(); onEvent(tech.id, evt.type, evt.amount); }}
                data-testid={`event-btn-${tech.id}-${evt.type}-${evt.amount || 1}`}
                className={`text-xs px-3 py-1.5 rounded text-white ${evt.color}`}>{evt.label}</button>
            ))}
            <button onClick={(e) => { e.stopPropagation(); onValidate(); }} data-testid={`validate-btn-${tech.id}`}
              className="text-xs px-3 py-1.5 rounded bg-pink-600 hover:bg-pink-500 text-white">✓ Validation coach (note)</button>
          </div>
        </div>
      )}
    </div>
  );
}

function MemberLinkSection({ profileId }) {
  const [link, setLink] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/progression/${profileId}/member-link`);
      setLink(data);
      setEmail(data.member_email || "");
    } catch { /* silencieux */ }
  }, [profileId]);

  useEffect(() => { setLink(null); load(); }, [load]);

  const save = async (regen = false) => {
    setBusy(true);
    try {
      const body = regen ? { regen_code: true } : { member_email: email };
      const { data } = await api.post(`/progression/${profileId}/member-link`, body);
      setLink({ ...link, member_email: data.member_email, link_code: data.link_code });
      toast.success(regen ? "Nouveau code généré ✓" : "Courriel lié ✓ — l'élève verra son carnet dans son Espace Membre");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };

  const copyCode = async () => {
    if (!link?.link_code) return;
    try { await navigator.clipboard.writeText(link.link_code); toast.success("Code copié"); } catch { /* */ }
  };

  return (
    <div className="mt-4 pt-4 border-t border-zinc-800" data-testid="member-link-section">
      <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-2">LIAISON ESPACE MEMBRE (PORTAIL ÉLÈVE)</div>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-end flex-wrap">
        <label className="flex-1 min-w-[220px]">
          <span className="text-xs text-zinc-400">Courriel de l'élève (liaison automatique)</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="eleve@courriel.com"
            data-testid="member-link-email-input" className="mt-1 w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-1.5 text-sm" />
        </label>
        <button onClick={() => save(false)} disabled={busy} data-testid="member-link-save-btn"
          className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-black rounded-lg text-sm font-semibold disabled:opacity-50">
          {busy ? "…" : "Lier le courriel"}
        </button>
      </div>
      <div className="mt-3 flex items-center gap-3 flex-wrap text-sm">
        <span className="text-zinc-400">Code de liaison :</span>
        <span className="font-mono font-bold text-amber-300 tracking-[0.2em] bg-zinc-950 border border-zinc-700 rounded px-3 py-1" data-testid="member-link-code">
          {link?.link_code || "…"}
        </span>
        <button onClick={copyCode} className="text-xs text-zinc-400 hover:text-amber-300 underline" data-testid="member-link-copy-code">Copier</button>
        <button onClick={() => save(true)} disabled={busy} className="text-xs text-zinc-500 hover:text-amber-300" data-testid="member-link-regen">↻ Régénérer</button>
      </div>
      <p className="text-[11px] text-zinc-500 mt-2">
        L'élève voit automatiquement son carnet s'il se connecte à l'Espace Membre avec ce courriel. Sinon, il entre ce code dans « Mon carnet ».
      </p>
    </div>
  );
}

function NewProfileModal({ prefill, onClose, onCreated }) {
  const [form, setForm] = useState({
    student_id: prefill?.student_id || "", student_name: prefill?.student_name || "", age: prefill?.age ?? 16,
    discipline: prefill?.discipline || "muay_thai_kickboxing", current_belt: "", target_belt: "",
    objective_type: "recreatif", personality: "mixte", weekly_training_frequency: 2,
  });
  const [busy, setBusy] = useState(false);
  const belts = useBeltLadder(form.discipline, form.age);
  const lockedStudent = !!prefill;

  // Aligne les ceintures sur l'échelle chargée (1ère & 2e par défaut).
  useEffect(() => {
    if (!belts.length) return;
    setForm((f) => {
      const names = belts.map((b) => b.name);
      const cur = names.includes(f.current_belt) ? f.current_belt : names[0];
      const tar = names.includes(f.target_belt) ? f.target_belt : (names[1] || names[0]);
      return { ...f, current_belt: cur, target_belt: tar };
    });
  }, [belts]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.student_name || !form.student_id) { toast.error("Nom et ID requis"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/progression/profile", {
        ...form, age: Number(form.age), weekly_training_frequency: Number(form.weekly_training_frequency),
      });
      toast.success("Carnet créé ✓");
      const key = data.profile.student_id || `name:${(data.profile.student_name || "").toLowerCase()}`;
      onCreated(data.profile.id, key);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-lg w-full space-y-3" data-testid="new-profile-modal">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold">{lockedStudent ? `Nouveau carnet — ${form.student_name}` : "Nouveau carnet de progression"}</h2>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <Input label="ID élève (interne)" value={form.student_id} onChange={(v) => setForm({ ...form, student_id: v })} placeholder="ex: jeremy-tremblay-001" testId="np-id" disabled={lockedStudent} />
        <Input label="Nom complet" value={form.student_name} onChange={(v) => setForm({ ...form, student_name: v })} placeholder="Jérémy Tremblay" testId="np-name" disabled={lockedStudent} />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Âge" type="number" value={form.age} onChange={(v) => setForm({ ...form, age: v })} testId="np-age" />
          <Select label="Discipline" value={form.discipline} onChange={(v) => setForm({ ...form, discipline: v })}
            options={DISCIPLINES.map((d) => ({ value: d.value, label: d.label }))} testId="np-disc" disabled={lockedStudent} />
        </div>
        <div className="grid grid-cols-2 gap-3" data-testid="belt-selectors">
          <BeltSelect label="Ceinture actuelle" value={form.current_belt} onChange={(v) => setForm({ ...form, current_belt: v })} belts={belts} testId="np-cur" />
          <BeltSelect label="Ceinture visée" value={form.target_belt} onChange={(v) => setForm({ ...form, target_belt: v })} belts={belts} testId="np-tar" />
        </div>
        <p className="text-[11px] text-zinc-500" data-testid="belt-scale-note">
          Échelle {form.discipline === "bjj" ? "Jiu-Jitsu Brésilien" : (Number(form.age) < 18 ? "Junior (7-17 ans)" : "Adulte (18 ans +)")} · {belts.length} ceintures officielles
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Select label="Objectif" value={form.objective_type} onChange={(v) => setForm({ ...form, objective_type: v })} options={[
            { value: "recreatif", label: "Récréatif" }, { value: "competitif", label: "Compétitif" }
          ]} testId="np-obj" />
          <Select label="Personnalité" value={form.personality} onChange={(v) => setForm({ ...form, personality: v })} options={[
            { value: "mixte", label: "Mixte / Inconnue" }, { value: "creatif", label: "Créative (aime explorer)" }, { value: "competitif", label: "Compétitive (aime les défis)" }
          ]} testId="np-pers" />
        </div>
        <Input label="Entraînements / semaine" type="number" value={form.weekly_training_frequency} onChange={(v) => setForm({ ...form, weekly_training_frequency: v })} testId="np-freq" />
        <button type="submit" disabled={busy} data-testid="np-submit"
          className="w-full bg-amber-600 hover:bg-amber-500 text-black font-bold py-2 rounded-lg disabled:opacity-50">
          {busy ? "Création..." : "Créer le carnet"}
        </button>
      </form>
    </div>
  );
}

function AddTechniqueModal({ profileId, discipline, age, onClose, onAdded }) {
  const [form, setForm] = useState({ technique_id: "", technique_name: "", category: "", belt_required: "" });
  const [busy, setBusy] = useState(false);
  const belts = useBeltLadder(discipline, age);

  useEffect(() => {
    if (belts.length && !form.belt_required) setForm((f) => ({ ...f, belt_required: belts[0].name }));
  }, [belts]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!form.technique_name || !form.belt_required) { toast.error("Nom et ceinture requis"); return; }
    setBusy(true);
    try {
      await api.post(`/progression/profile/${profileId}/technique`, {
        ...form, technique_id: form.technique_id || `manual-${Date.now()}`,
      });
      toast.success("Technique ajoutée ✓");
      onAdded();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-md w-full space-y-3" data-testid="add-technique-modal">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold">Ajouter une technique</h2>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <Input label="Nom de la technique" value={form.technique_name} onChange={(v) => setForm({ ...form, technique_name: v })} placeholder="ex: Kimura garde fermée" testId="at-name" />
        <Input label="Catégorie" value={form.category} onChange={(v) => setForm({ ...form, category: v })} placeholder="soumission · projection · frappe..." testId="at-cat" />
        <BeltSelect label="Ceinture associée" value={form.belt_required} onChange={(v) => setForm({ ...form, belt_required: v })} belts={belts} testId="at-belt" />
        <button type="submit" disabled={busy} data-testid="at-submit"
          className="w-full bg-amber-600 hover:bg-amber-500 text-black font-bold py-2 rounded-lg disabled:opacity-50">
          {busy ? "Ajout..." : "Ajouter"}
        </button>
      </form>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, testId, disabled }) {
  return (
    <label className="block">
      <span className="text-xs text-zinc-400">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} disabled={disabled}
        className="mt-1 w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-1.5 text-sm disabled:opacity-60" />
    </label>
  );
}

function BeltSelect({ label, value, onChange, belts, testId }) {
  const list = belts || [];
  return (
    <label className="block">
      <span className="text-xs text-zinc-400">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="mt-1 w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-1.5 text-sm">
        {list.length === 0 && <option value={value}>{value || "…"}</option>}
        {list.map((b) => (
          <option key={b.name} value={b.name} disabled={b.locked}>
            {b.name}{b.locked ? " 🔒 (18 ans +)" : ""}
          </option>
        ))}
      </select>
    </label>
  );
}

function Select({ label, value, onChange, options, testId, disabled }) {
  return (
    <label className="block">
      <span className="text-xs text-zinc-400">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} disabled={disabled}
        className="mt-1 w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-1.5 text-sm disabled:opacity-60">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
